import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Column } from 'components/display/Table/types';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import GenericBadge from 'components/ui/Badge';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import {
  DropdownButton,
  useActionMenu
} from 'components/navigation/Dropdown/GenericDropdownButton';
import {
  ContractIdentifier,
  ContractRow,
  ContractType
} from 'features/contract-view/types/interfaces';
import { ContractTableRowKey } from 'features/contract-view/types/enums';
import { Status } from 'types/optim/status';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import { ContractFetchParams } from 'slices/generic-table/hooks/useContractTable';
import { mapContractSummaries } from 'features/contract-view/mappers/mapContractSummaries';
import { GetSummaryContractListResponse, GetGenericListCommandDto } from 'api/contract';
import { contractsAPI } from 'services/swagger-api';
import { TableResponse } from 'slices/generic-table/genericTableSlice';
import { FileText, FolderOpen } from 'lucide-react';
import GenericTabs from 'components/navigation/Tabs/Tabs';
import { PersonGroupContracts } from '../person-group-contracts/PersonGroupContracts';
import { useDocumentViewerDialog } from 'hooks/useDocumentViewerDialog';
import { DocumentScopeEnum } from 'api/documents';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';

interface PersonContractsProps {
  personId: string;
}

export const PersonContracts: React.FC<PersonContractsProps> = ({ personId }) => {
  // Menu d'écriture (résilier, attestation…) : Courtier uniquement (matrice centrale).
  const canWriteActions = useRoleCan(Capability.ContractWriteActions);
  const { t } = useDynamicTranslation([
    NsKey.Common,
    NsKey.Contract,
    NsKey.Risk,
    NsKey.Pricing
  ]);
  const navigate = useNavigate();
  const { createMenuItem } = useActionMenu();
  const { openDocumentViewer } = useDocumentViewerDialog();

  const goToContractDetails = useCallback(
    (contractId: string, type: ContractType) => {
      navigate(`/mes-contrats/details/${contractId}`, {
        state: { type }
      });
    },
    [navigate]
  );

  const columns: Column<ContractRow>[] = useMemo(
    () => [
      {
        key: ContractTableRowKey.DATE,
        label: t('COMMON.DATE'),
        sortable: true,
        render: (value) => {
          const date = new Date(value as string);
          const day = date.getDate();
          const month = date.toLocaleDateString('fr-FR', { month: 'short' });
          const year = date.getFullYear();
          const time = date.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
          });

          return (
            <div className="text-left">
              <div className="text-sm font-medium">
                {day} {month} {year}
              </div>
              <div className="text-xs text-gray-500">{time}</div>
            </div>
          );
        }
      },
      {
        key: ContractTableRowKey.CONTRACT_IDENTIFIER,
        label: t('CONTRACT.LABELS.CONTRACT_NUMBER'),
        render: (value) => {
          const contractIdentifier = value as ContractIdentifier;
          if (!contractIdentifier.id) {
            return <></>;
          }

          return (
            <div
              onClick={() =>
                goToContractDetails(
                  contractIdentifier.id,
                  contractIdentifier.type
                )
              }
              className="cursor-pointer font-bold text-primary hover:underline"
            >
              {contractIdentifier && contractIdentifier.label}
            </div>
          );
        }
      },
      {
        key: ContractTableRowKey.PRODUCT,
        label: t('COMMON.LABELS.PRODUCT')
      },
      {
        key: ContractTableRowKey.SUPPORT,
        label: t('COMMON.LABELS.SUPPORT')
      },
      {
        key: ContractTableRowKey.OWNER,
        label: t('COMMON.LABELS.INSURER')
      },
      {
        key: ContractTableRowKey.CLIENT_PRICE,
        label: t('PRICING.LABELS.CLIENT_PRICE')
      },
      {
        key: ContractTableRowKey.STATUS,
        label: t('COMMON.STATUS'),
        sortable: true,
        render: (value) => {
          const status = value as Status;
          return (
            <GenericBadge
              label={status.label}
              color={status.color}
              badgeType="sm-bold"
            />
          );
        }
      }
    ],
    [t, goToContractDetails]
  );

  const getMoreMenuActions = useCallback(
    (row: ContractRow) => [
      {
        options: {
          icon: 'fileText'
        },
        key: 'viewDocuments',
        label: t('CONTRACT.ACTIONS.VIEW_DOCUMENTS'),
        action: () => {
          openDocumentViewer({
            strategy: DocumentScopeEnum.Contract,
            entityId: row.contractIdentifier.id
          });
        }
      },
      {
        options: {
          icon: 'arrowRightLeft'
        },
        key: 'terminateContract',
        label: t('CONTRACT.ACTIONS.TERMINATE_CONTRACT'),
        action: () => {}
      },
      {
        options: {
          icon: 'arrowRightLeft'
        },
        key: 'issueCertificate',
        label: t('CONTRACT.ACTIONS.ISSUE_CERTIFICATE'),
        action: () => {}
      }
    ],
    [t, openDocumentViewer]
  );

  const getMenuItems = useCallback(
    (row: ContractRow) => {
      return getMoreMenuActions(row).map((action) =>
        createMenuItem(action.key, action.label, action.action, action.options)
      );
    },
    [getMoreMenuActions, createMenuItem]
  );

  const actions = useMemo(
    () => ({
      row: [
        // Œil (consultation) : tous les rôles ; menu d'écriture : Courtier uniquement.
        {
          label: 'seeMore',
          render: (row: ContractRow, index: number) => (
            <Button
              key={index}
              color="slate"
              round
              onClick={() =>
                goToContractDetails(
                  row.contractIdentifier.id,
                  row.contractIdentifier.type
                )
              }
            >
              <EyeIcon />
            </Button>
          )
        },
        ...(canWriteActions
          ? [
              {
                label: 'moreMenu',
                render: (row: ContractRow, index: number) => (
                  <DropdownButton
                    key={index}
                    width="w-64"
                    anchor="bottom"
                    items={getMenuItems(row)}
                  />
                )
              }
            ]
          : [])
      ]
    }),
    [goToContractDetails, getMenuItems, canWriteActions]
  );

  const fetchContracts = useCallback(
    async (params: ContractFetchParams): Promise<TableResponse<ContractRow>> => {
      try {
        const command: GetGenericListCommandDto = {
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          searchString: params.searchString || undefined,
          sorting: params.sorting as any,
          filters: {
            personIds: [personId],
          }
        };

        const result: GetSummaryContractListResponse =
          await contractsAPI.getSummaryContracts({
            getGenericListCommandDto: command
          });

        const mappedContracts = mapContractSummaries(result.items || []);

        return {
          items: mappedContracts,
          totalItems: result.totalCount || mappedContracts.length,
          totalPages: Math.ceil(
            (result.totalCount || mappedContracts.length) / params.pageSize
          ),
          currentPage: params.pageNumber
        };
      } catch (error) {
        console.error('Erreur lors de la récupération des contrats:', error);
        throw error;
      }
    },
    [personId]
  );

  // Onglet « Contrats collectifs » : visible uniquement si le client est souscripteur
  // d'au moins un contrat de groupe (comptage au montage, scope courtier côté back).
  const [groupContractsCount, setGroupContractsCount] = useState(0);
  const [groupContractsCountFailed, setGroupContractsCountFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    contractsAPI
      .getGroupContractsCourtier({
        getGenericListCommandDto: {
          pageNumber: 1,
          pageSize: 1,
          filters: { personIds: [personId] }
        }
      })
      .then((response) => {
        if (!cancelled) {
          setGroupContractsCount(response.totalCount || 0);
          setGroupContractsCountFailed(false);
        }
      })
      .catch((error) => {
        console.error('Erreur lors du comptage des contrats collectifs:', error);
        // Échec du comptage (timeout, 500 transitoire…) : on affiche quand même
        // l'onglet plutôt que de cacher silencieusement d'éventuels contrats —
        // la table de l'onglet refait sa propre requête et gère son état d'erreur.
        if (!cancelled) setGroupContractsCountFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [personId]);

  const individualContractsTable = (
    <ServerTable<ContractRow>
      apiConfig={{
        fetchData: fetchContracts
      }}
      columns={columns}
      actions={actions}
      onRowClick={(row) =>
        goToContractDetails(row.contractIdentifier.id, row.contractIdentifier.type)
      }
    />
  );

  if (groupContractsCount > 0 || groupContractsCountFailed) {
    return (
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-4">
          <GenericTabs defaultTab={1}>
            <GenericTabs.Item
              label={t('FEATURES.TITLE.MY_CONTRACTS')}
              icon={<FileText className="w-4 h-4" />}
              value={1}
            />
            <GenericTabs.Item
              label="Contrats collectifs"
              icon={<FolderOpen className="w-4 h-4" />}
              badge={groupContractsCount > 0 ? groupContractsCount : undefined}
              value={2}
            />

            <GenericTabs.Content value={1}>
              {individualContractsTable}
            </GenericTabs.Content>
            <GenericTabs.Content value={2}>
              <PersonGroupContracts personId={personId} />
            </GenericTabs.Content>
          </GenericTabs>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border">
      <div className="flex items-center gap-3 p-4 border-b">
        <FileText className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-semibold text-gray-900">
          {t('FEATURES.TITLE.MY_CONTRACTS')}
        </h3>
      </div>
      <div className="p-4">{individualContractsTable}</div>
    </div>
  );
};
